import React, { useMemo } from 'react';
import styled from 'styled-components';
import { ModalShell } from '../../ui/shell';
import {
  Btn,
  Field,
  FormGrid,
  Input,
  SetRow,
  Switch,
} from '../../ui/primitives';
import { ButtonSpinnerSlot } from '../../ui/ButtonSpinner';
import {
  formatEntitlementLabel,
  formatPlanLabel,
  formatUsdFromCents,
  groupEntitlements,
  parseDollarsToCents,
} from './planDisplayUtils';
import { PlanEntitlementOverview } from './PlanEntitlementSummary';

const Section = styled.div`
  &:not(:first-child) {
    margin-top: 22px;
    padding-top: 4px;
    border-top: 1px solid ${({ theme }) => theme.border};
  }
`;

const SectionTitle = styled.h4`
  margin: 0 0 4px;
  font-family: ${({ theme }) => theme.fontHead};
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.text};
`;

const SectionHint = styled.p`
  margin: 0 0 14px;
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.textMuted};
  line-height: 1.45;
`;

const EntitlementGrid = styled(FormGrid)`
  margin-bottom: 4px;
`;

/**
 * Super-admin plan editor — pricing + full entitlement matrix (ModalShell / on-brand).
 */
export default function PlanPackageDialog({
  open,
  planKind,
  form,
  onChange,
  onEntitlementChange,
  onSubmit,
  onClose,
  loading,
}) {
  const grouped = useMemo(
    () => groupEntitlements(form.entitlements),
    [form.entitlements],
  );

  if (!open) {
    return null;
  }

  const planTitle = formatPlanLabel(form.name || form.tier);
  const dialogTitle = planKind === 'learner' ? 'Edit learner plan' : 'Edit instructor plan';

  const renderEntitlementField = (item, indexInAll) => (
    <Field key={`${item.kind}:${item.entKey}`}>
      <label htmlFor={`ent-${item.entKey}`}>{formatEntitlementLabel(item.entKey)}</label>
      <Input
        id={`ent-${item.entKey}`}
        value={item.value ?? ''}
        onChange={(event) => onEntitlementChange(indexInAll, event.target.value)}
      />
    </Field>
  );

  const findEntitlementIndex = (target) => (
    form.entitlements.findIndex(
      (item) => item.entKey === target.entKey && item.kind === target.kind,
    )
  );

  return (
    <ModalShell
      title={dialogTitle}
      sub={`${planTitle} · full pricing and capability control`}
      onClose={onClose}
      max="640px"
      foot={(
        <>
          <Btn type="button" $variant="ghost" onClick={onClose} disabled={loading}>
            Cancel
          </Btn>
          {loading ? (
            <ButtonSpinnerSlot size={24} />
          ) : (
            <Btn type="button" onClick={onSubmit}>
              Save plan
            </Btn>
          )}
        </>
      )}
    >
      <Section>
        <SectionTitle>Plan</SectionTitle>
        <SectionHint>Tier slug is fixed; update the display name shown in catalogs.</SectionHint>
        <FormGrid>
          <Field>
            <label htmlFor="plan-tier">Tier</label>
            <Input id="plan-tier" value={formatPlanLabel(form.tier)} readOnly />
          </Field>
          <Field>
            <label htmlFor="plan-name">Display name</label>
            <Input
              id="plan-name"
              value={form.name || ''}
              onChange={(event) => onChange({ ...form, name: event.target.value })}
            />
          </Field>
        </FormGrid>
      </Section>

      <Section>
        <SectionTitle>Pricing</SectionTitle>
        <SectionHint>Values stored in the plan pricing table (cents on save).</SectionHint>
        <FormGrid>
          <Field>
            <label htmlFor="plan-monthly">Monthly (USD)</label>
            <Input
              id="plan-monthly"
              value={formatUsdFromCents(form.monthlyCents)}
              onChange={(event) => onChange({
                ...form,
                monthlyCents: parseDollarsToCents(event.target.value),
              })}
            />
          </Field>
          <Field>
            <label htmlFor="plan-annual">Annual (USD)</label>
            <Input
              id="plan-annual"
              value={formatUsdFromCents(form.annualCents)}
              onChange={(event) => onChange({
                ...form,
                annualCents: parseDollarsToCents(event.target.value),
              })}
            />
          </Field>
          <Field>
            <label htmlFor="plan-currency">Currency</label>
            <Input
              id="plan-currency"
              value={form.currency || 'usd'}
              onChange={(event) => onChange({ ...form, currency: event.target.value })}
            />
          </Field>
        </FormGrid>
      </Section>

      <Section>
        <SectionTitle>Plan allocation</SectionTitle>
        <SectionHint>What this tier includes — limits and enabled features from plan entitlements.</SectionHint>
        <PlanEntitlementOverview
          entitlements={form.entitlements}
          planKind={planKind}
        />
      </Section>

      {grouped.limits.length > 0 && (
        <Section>
          <SectionTitle>Limits</SectionTitle>
          <SectionHint>Integer meters (courses, learners, assessments, …).</SectionHint>
          <EntitlementGrid>
            {grouped.limits.map((item) => renderEntitlementField(item, findEntitlementIndex(item)))}
          </EntitlementGrid>
        </Section>
      )}

      {grouped.features.length > 0 && (
        <Section>
          <SectionTitle>Features</SectionTitle>
          <SectionHint>Boolean capabilities enabled for this plan.</SectionHint>
          {grouped.features.map((item) => {
            const indexInAll = findEntitlementIndex(item);
            const isOn = String(item.value).toLowerCase() === 'true';
            return (
              <SetRow key={`${item.kind}:${item.entKey}`}>
                <div className="set-row__txt">
                  <h4>{formatEntitlementLabel(item.entKey)}</h4>
                  <p>{item.entKey}</p>
                </div>
                <Switch
                  role="switch"
                  aria-checked={isOn}
                  $on={isOn}
                  onClick={() => onEntitlementChange(indexInAll, isOn ? 'false' : 'true')}
                />
              </SetRow>
            );
          })}
        </Section>
      )}

      {(grouped.text.length > 0 || grouped.other.length > 0) && (
        <Section>
          <SectionTitle>Configuration</SectionTitle>
          <SectionHint>Text and other entitlement values.</SectionHint>
          <EntitlementGrid>
            {[...grouped.text, ...grouped.other].map(
              (item) => renderEntitlementField(item, findEntitlementIndex(item)),
            )}
          </EntitlementGrid>
        </Section>
      )}
    </ModalShell>
  );
}
